/**
 * Split an answer into its numbered findings.
 *
 * A finding's quotes and closing line belong to its number, but the writer
 * emits them flush left and markdown then reads them as siblings of the list —
 * so they rendered beside the findings instead of under them. Rather than
 * depend on the model indenting continuation blocks exactly right, the answer
 * is split here and each finding is rendered as its own row, which makes the
 * hierarchy a fact of the DOM.
 *
 * Only a run of numbers starting at 1 and counting up is treated as findings,
 * so a stray "1." inside a finding's body cannot start a new one.
 */
const ITEM = /^(\*\*)?(\d{1,2})[.)]\s+(.*)$/;

/** @returns {{ intro: string, findings: Array<{ n: number, body: string }> }} */
export const splitFindings = (text) => {
  const lines = String(text || "").split("\n");
  const intro = [];
  const found = [];
  let current = null;
  let expected = 1;

  for (const line of lines) {
    const m = ITEM.exec(line);
    if (m && Number(m[2]) === expected) {
      if (current) found.push(current);
      // "**1. Claim.** body" keeps its bold: the marker goes back on the front.
      current = { n: expected, lines: [(m[1] || "") + m[3]] };
      expected += 1;
      continue;
    }
    // Continuation lines are de-indented, so a body the writer indented by
    // three spaces is not read as an indented code block on its own.
    if (current) current.lines.push(line.replace(/^ {1,3}/, ""));
    else intro.push(line);
  }
  if (current) found.push(current);

  return {
    intro: intro.join("\n").trim(),
    findings: found.map((f) => ({ n: f.n, body: f.lines.join("\n").trim() })),
  };
};
